import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  loadPowerBISecurityDataRequest,
  loadPowerBISecurityAddDataRequest,
  loadPowerBISecurityUpdateDataRequest,
  loadPowerBISecurityDeleteDataRequest,
  loadPowerBISecurityUpdateSequenceRequest,
  loadPowerBISecurityGetReportPagesRequest,
} from '../redux/Actions';
import { loadPowerBIAllReportsDataRequest } from '../../power-bi-portal/redux/Actions';
import Breadcrumbs from '../../../shared/components/Breadcrumbs';
import PowerBISecurityModal from './PowerBISecurityModal';
import SyncPagesModal from './SyncPagesModal';
import './PowerBISecurity.css';
import DefaultButton from '../../../shared/components/DefaultButton';
import { CREATENEW } from '../../../shared/constants/Constants';

const PowerBISecurityInline = () => {
  const dispatch = useDispatch();
  const { loading, securityList, error } = useSelector((state) => state.powerBISecurity);
  const { allReports = [] } = useSelector((state) => state.powerBIReports);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedItem, setSelectedItem] = useState(null);
  const [savingRowId, setSavingRowId] = useState(null); // Track which row is being saved
  const [localList, setLocalList] = useState([]); // Local state for optimistic updates

  // Sync Pages Modal
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedReportForSync, setSelectedReportForSync] = useState(null);

  useEffect(() => {
    dispatch(loadPowerBISecurityDataRequest());
    dispatch(loadPowerBIAllReportsDataRequest());
  }, [dispatch]);

  // Sync local list with Redux state
  useEffect(() => {
    if (securityList && securityList.length > 0) {
      setLocalList(securityList);
    }
  }, [securityList]);

  // Handle drag end - auto-save
  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const items = Array.from(localList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Optimistically update UI
    setLocalList(items);
    setSavingRowId(reorderedItem.id);

    // Prepare updates array with ONLY items whose sequence changed
    const updates = [];
    items.forEach((item, index) => {
      const newSequence = index + 1;
      const oldSequence = securityList.findIndex(s => s.id === item.id) + 1;

      // Only include if sequence actually changed
      if (newSequence !== oldSequence) {
        updates.push({
          id: item.id,
          sequence: newSequence
        });
      }
    });

    try {
      // Auto-save to API - only changed items
      await dispatch(loadPowerBISecurityUpdateSequenceRequest(updates));
      setSavingRowId(null);
    } catch (error) {
      // Revert on error
      setLocalList(securityList);
      setSavingRowId(null);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this security configuration?')) {
      dispatch(loadPowerBISecurityDeleteDataRequest(id));
    }
  };

  const handleAddNew = () => {
    setModalMode('add');
    setSelectedItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setModalMode('edit');
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  const handleSaveModal = (formData) => {
    if (modalMode === 'add') {
      // Auto-assign sequence as the next number (append to end)
      const maxSequence = Math.max(...localList.map(item => item.sequence || 0), 0);
      const dataWithSequence = {
        ...formData,
        sequence: maxSequence + 1
      };
      dispatch(loadPowerBISecurityAddDataRequest(dataWithSequence));
    } else if (modalMode === 'edit' && selectedItem) {
      // Keep existing sequence when editing
      const dataWithSequence = {
        ...formData,
        sequence: selectedItem.sequence
      };
      dispatch(loadPowerBISecurityUpdateDataRequest(selectedItem.id, dataWithSequence));
    }
    setShowModal(false);
    setSelectedItem(null);
  };

  const handleSyncPages = (item) => {
    // Check if show_pages is enabled
    if (!item.show_pages) {
      alert('Page sync is only available for reports with "Show Pages" enabled.');
      return;
    }

     // Find the full report from allReports
    const fullReport = allReports.find(
      r => (r.id || r.report_id || r.reportId) === item.report_id
    );

    // Dispatch action to fetch report pages by report_id
    dispatch(loadPowerBISecurityGetReportPagesRequest(item.report_id));

    // Merge the security item with full report data (embedUrl, datasetId, group_role, etc.)
    const mergedReportData = {
      ...fullReport,  // This has embedUrl, datasetId, group_role, etc.
      ...item,            // This has id (registry id) and security settings
    };

    console.log('handleSyncPages - Merged report data:', mergedReportData);

    // Set the merged report data for the sync modal
    setSelectedReportForSync(mergedReportData);
    setShowSyncModal(true);
  };

  const handleCloseSyncModal = () => {
    setShowSyncModal(false);
    setSelectedReportForSync(null);
  };

  return (
    <div className="powerbi-security-container">
      <div className="row mb-3">
        <div className="col-md-6">
          <Breadcrumbs
            children={[
              { href: '/powerbi-security', label: 'Power BI Security' },
            ]}
          />
        </div>
        <div className="col-md-6 text-end">
          <DefaultButton
            buttonClass="default-btn-color"
            buttonTitle={CREATENEW}
            actionBlock={handleAddNew}
          />
        </div>
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="alert alert-danger" role="alert">
          <i className="fa fa-exclamation-triangle me-2"></i>
          Failed to load security configurations
        </div>
      )}

      {!loading && !error && (
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <DragDropContext onDragEnd={handleDragEnd}>
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: '30px' }}></th>
                      <th>ICON</th>
                      <th>REPORT NAME</th>
                      <th>REPORT ID</th>
                      <th className="text-center">SHOW PAGES</th>
                      <th>ACCESS SUMMARY</th>
                      <th className="text-center">SYNC PAGES</th>
                      <th className="text-center">ACTIONS</th>
                    </tr>
                  </thead>
                  <Droppable droppableId="security-list">
                    {(provided) => (
                      <tbody {...provided.droppableProps} ref={provided.innerRef}>
                        {localList.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              <i className="fa fa-info-circle me-2"></i>
                              No security configurations found. Click "Add New" to create one.
                            </td>
                          </tr>
                        ) : (
                          localList.map((item, index) => {
                            const groupRoles = item.group_role || [];
                            const reportIcon = item.report_icon || item.icon || 'bar-chart';
                            const colorCode = '#1a2332';
                            const isSaving = savingRowId === item.id;

                            return (
                              <Draggable
                                key={String(item.id)}
                                draggableId={String(item.id)}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <tr
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      backgroundColor: snapshot.isDragging ? '#f8f9fa' : 'white',
                                      opacity: isSaving ? 0.6 : 1,
                                    }}
                                  >
                                    <td {...provided.dragHandleProps} style={{ cursor: 'grab', textAlign: 'center' }}>
                                      <i className="fa fa-bars" style={{ color: '#6c757d' }}></i>
                                    </td>
                                    <td>
                                      <div style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: colorCode,
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        <i className={`fa fa-${reportIcon}`} style={{ fontSize: '1.2rem', color: '#ffffff' }}></i>
                                      </div>
                                    </td>
                                    <td>{item.report_name || item.reportName}</td>
                                    <td><code className="text-muted small">{item.report_id || item.reportId}</code></td>
                                    <td className="text-center">
                                      {item.show_pages ? (
                                        <span className="badge bg-success">
                                          <i className="fa fa-check me-1"></i>
                                          Enabled
                                        </span>
                                      ) : (
                                        <span className="badge bg-secondary">
                                          <i className="fa fa-times me-1"></i>
                                          Disabled
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ maxWidth: '600px' }}>
                                      {groupRoles.length === 0 ? (
                                        <span className="text-muted">No access configured</span>
                                      ) : (
                                        <div className="d-flex flex-wrap" style={{ gap: '6px', rowGap: '6px' }}>
                                          {groupRoles.map((gr, index) => {
                                            const groupName = gr.group_name || gr.group;
                                            const roleLabel = gr.role_label || gr.role;
                                            const icon = gr.icon || 'users';
                                            const color = gr.color || '#d1fae5';

                                            return (
                                              <span
                                                key={index}
                                                style={{
                                                  backgroundColor: '#f1f8f0',
                                                  border: `1px solid ${color}`,
                                                  color: '#065f46',
                                                  padding: '3px 8px',
                                                  borderRadius: '4px',
                                                  fontSize: '0.75rem',
                                                  fontWeight: '600',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: '4px',
                                                  whiteSpace: 'nowrap'
                                                }}
                                              >
                                                <i className={`fa fa-${icon}`} aria-hidden="true" style={{ fontSize: '0.7rem' }}></i>
                                                <span>{groupName} • {roleLabel}</span>
                                              </span>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      <button
                                        className={`btn btn-sm ${item.show_pages ? 'btn-outline-info' : 'btn-outline-secondary'}`}
                                        onClick={() => handleSyncPages(item)}
                                        title={item.show_pages ? "Sync Pages" : "Enable Show Pages to sync"}
                                        disabled={isSaving || !item.show_pages}
                                      >
                                        <i className="fa fa-refresh" aria-hidden="true"></i>
                                      </button>
                                    </td>
                                    <td className="text-center">
                                      <button
                                        className="btn btn-sm btn-outline-primary me-2"
                                        onClick={() => handleEdit(item)}
                                        title="Edit"
                                        disabled={isSaving}
                                      >
                                        <i className="fa fa-pencil" aria-hidden="true"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(item.id)}
                                        title="Delete"
                                        disabled={isSaving}
                                      >
                                        <i className="fa fa-trash" aria-hidden="true"></i>
                                      </button>
                                      {isSaving && (
                                        <div className="spinner-border spinner-border-sm text-primary ms-2" role="status">
                                          <span className="visually-hidden">Saving...</span>
                                        </div>
                                      )}
                                    </td>
                                </tr>
                              )}
                            </Draggable>
                          );
                        })
                      )}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </table>
            </DragDropContext>
          </div>
        </div>
      </div>
      )}

      {/* Modal for Add/Edit */}
      <PowerBISecurityModal
        show={showModal}
        handleClose={handleCloseModal}
        handleSave={handleSaveModal}
        mode={modalMode}
        initialData={selectedItem}
        allReports={allReports}
        securityList={securityList}
      />

      {/* Modal for Sync Pages */}
      <SyncPagesModal
        show={showSyncModal}
        handleClose={handleCloseSyncModal}
        reportData={selectedReportForSync}
      />
    </div>
  );
};

export default PowerBISecurityInline;
